import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Tournament, TournamentSettings, TournamentStatus, Round, Prize, GameType } from '@/types';

interface TournamentSliceState {
  current: Tournament | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: TournamentSliceState = {
  current: null,
  isLoading: false,
  error: null,
};

const createDefaultTournament = (): Tournament => {
  // Create default round structure
  const defaultRounds: Round[] = [
    { id: crypto.randomUUID(), minutes: 15, smallBlind: 25, bigBlind: 50, ante: 0, breakMinutes: 0 },
    { id: crypto.randomUUID(), minutes: 15, smallBlind: 50, bigBlind: 100, ante: 0, breakMinutes: 0 },
    { id: crypto.randomUUID(), minutes: 15, smallBlind: 75, bigBlind: 150, ante: 0, breakMinutes: 5 },
    { id: crypto.randomUUID(), minutes: 15, smallBlind: 100, bigBlind: 200, ante: 0, breakMinutes: 0 },
  ];

  return {
    id: crypto.randomUUID(),
    settings: {
      name: 'New Tournament',
      notes: '',
      gameType: GameType.NO_LIMIT,
      entryFee: 20,
      startingChips: 5000,
      maxPlayersPerTable: 8,
      fixedRake: 0,
      perPlayerRake: 0,
      currencySymbol: '$',
      allowRebuys: true,
      rebuyFee: 20,
      rebuyChips: 4000,
      disableRebuysAfterRound: 4,
      applyRakeToRebuys: false,
      allowAddOns: true,
      addOnFee: 5,
      addOnChips: 1000,
      disableAddOnsAfterRound: 4,
      applyRakeToAddOns: false,
      pauseBetweenRounds: false,
      handTimerDefaultSeconds: 60,
    },
    rounds: defaultRounds,
    players: [],
    prizes: [],
    state: {
      currentRound: 1,
      secondsRemaining: defaultRounds[0].minutes * 60, // Initialize with first round time
      accumulatedSeconds: 0,
      status: TournamentStatus.PENDING,
      pot: 0,
      rake: 0,
      totalEntries: 0,
      activePlayers: 0,
      totalRebuys: 0,
      totalAddOns: 0,
    },
    history: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
};

const tournamentSlice = createSlice({
  name: 'tournament',
  initialState,
  reducers: {
    createTournament: (state) => {
      const tournament = createDefaultTournament();
      state.current = tournament;
      state.error = null;
    },
    
    loadTournament: (state, action: PayloadAction<Tournament>) => {
      state.current = action.payload;
      state.error = null;
    },
    
    updateSettings: (state, action: PayloadAction<Partial<TournamentSettings>>) => {
      if (state.current) {
        state.current.settings = { ...state.current.settings, ...action.payload };
        state.current.updatedAt = new Date();
      }
    },
    
    setStatus: (state, action: PayloadAction<TournamentStatus>) => {
      if (state.current) {
        state.current.state.status = action.payload;
        state.current.updatedAt = new Date();
      }
    },
    
    setCurrentRound: (state, action: PayloadAction<number>) => {
      if (state.current) {
        state.current.state.currentRound = action.payload;
        state.current.updatedAt = new Date();
      }
    },
    
    updateRounds: (state, action: PayloadAction<Round[]>) => {
      if (state.current) {
        state.current.rounds = action.payload;
        state.current.updatedAt = new Date();
      }
    },
    
    addRound: (state, action: PayloadAction<Round>) => {
      if (state.current) {
        state.current.rounds.push(action.payload);
        state.current.updatedAt = new Date();
      }
    },
    
    updateRound: (state, action: PayloadAction<{ index: number; round: Round }>) => {
      if (state.current && state.current.rounds[action.payload.index]) {
        state.current.rounds[action.payload.index] = action.payload.round;
        state.current.updatedAt = new Date();
      }
    },
    
    deleteRound: (state, action: PayloadAction<number>) => {
      if (state.current) {
        state.current.rounds.splice(action.payload, 1);
        state.current.updatedAt = new Date();
      }
    },
    
    updatePrizes: (state, action: PayloadAction<Prize[]>) => {
      if (state.current) {
        state.current.prizes = action.payload;
        state.current.updatedAt = new Date();
      }
    },
    
    addPrize: (state, action: PayloadAction<Prize>) => {
      if (state.current) {
        state.current.prizes.push(action.payload);
        state.current.updatedAt = new Date();
      }
    },
    
    updatePot: (state, action: PayloadAction<number>) => {
      if (state.current) {
        state.current.state.pot = action.payload;
      }
    },
    
    updateRake: (state, action: PayloadAction<number>) => {
      if (state.current) {
        state.current.state.rake = action.payload;
      }
    },
    
    addHistoryEntry: (state, action: PayloadAction<string>) => {
      if (state.current) {
        const timestamp = new Date().toLocaleString();
        state.current.history.push(`${timestamp}: ${action.payload}`);
      }
    },
    
    setStartTime: (state, action: PayloadAction<Date | string>) => {
      if (state.current) {
        state.current.state.startTime = typeof action.payload === 'string' ? new Date(action.payload) : action.payload;
        state.current.state.status = TournamentStatus.ACTIVE;
      }
    },
    
    setEndTime: (state, action: PayloadAction<Date | string>) => {
      if (state.current) {
        state.current.state.endTime = typeof action.payload === 'string' ? new Date(action.payload) : action.payload;
        state.current.state.status = TournamentStatus.COMPLETED;
      }
    },
    
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    clearTournament: (state) => {
      state.current = null;
      state.error = null;
    },
  },
});

export const {
  createTournament,
  loadTournament,
  updateSettings,
  setStatus,
  setCurrentRound,
  updateRounds,
  addRound,
  updateRound,
  deleteRound,
  updatePrizes,
  addPrize,
  updatePot,
  updateRake,
  addHistoryEntry,
  setStartTime,
  setEndTime,
  setLoading,
  setError,
  clearTournament,
} = tournamentSlice.actions;

export default tournamentSlice.reducer;